import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { useEnquiry } from "@/context/EnquiryContext";
import { useContact } from "@/hooks/useContact";
import { usePages } from "@/hooks/usePages";
import { T } from "@/context/ContentContext";
import { ExploreMenu, MobileExplore } from "./ExploreMenu";

const FALLBACK_LINKS = [
  { to: "/georgia", label: "Georgia" },
  { to: "/trips", label: "All Trips" },
  { to: "/trips?region=india", label: "India" },
  { to: "/trips?region=international", label: "International" },
  { to: "/contact", label: "Contact" },
];

function useMenuLinks() {
  const { menu, loaded } = usePages();
  return loaded ? menu.map((p) => ({ to: p.path, label: p.name })) : FALLBACK_LINKS;
}

export function Navbar() {
  const { openEnquiry } = useEnquiry();
  const CONTACT = useContact();
  const LINKS = useMenuLinks();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="fixed top-0 inset-x-0 z-50 px-3 sm:px-6 pt-3">
      <nav data-testid="navbar"
        className={`mx-auto max-w-7xl flex items-center justify-between rounded-full pl-4 pr-2 py-2 border border-ink/10 bg-paper transition-shadow duration-500 ${
          scrolled ? "shadow-[0_10px_40px_-20px_rgba(21,17,14,0.35)]" : "shadow-[0_8px_30px_-18px_rgba(0,0,0,0.5)]"}`}>
        <Link to="/" data-testid="navbar-logo" onClick={() => setOpen(false)}><Logo /></Link>
        <div className="hidden lg:flex items-center gap-1">
          <ExploreMenu />
          {LINKS.map((l) => (
            <NavLink key={l.label} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="relative px-4 py-2 text-sm font-medium text-ink/80 hover:text-ink transition-colors group">
              {l.label}
              <span className="absolute left-4 right-4 bottom-1 h-px bg-terra scale-x-0 origin-left transition-transform duration-300 group-hover:scale-x-100" />
            </NavLink>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <a href={`tel:+91${CONTACT.phone}`} data-testid="navbar-call-link" className="hidden xl:inline-flex items-center gap-2 px-3 text-sm font-mono text-ink/80 hover:text-terra transition-colors">
            <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
          </a>
          <button onClick={() => openEnquiry()} data-testid="navbar-enquire-btn"
            className="rounded-full bg-ink text-cream px-5 py-2.5 text-sm font-semibold transition-[background-color,transform] duration-300 hover:bg-terra active:scale-95">
            <T k="nav.enquire">Enquire</T>
          </button>
          <button onClick={() => setOpen((o) => !o)} data-testid="navbar-menu-toggle" aria-label="Menu"
            className="lg:hidden grid place-items-center h-10 w-10 rounded-full border border-ink/15">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            data-testid="mobile-menu"
            className="lg:hidden mx-auto max-w-7xl mt-2 rounded-3xl bg-paper text-ink border border-ink/10 shadow-xl p-6 flex flex-col gap-1 max-h-[80vh] overflow-y-auto" data-lenis-prevent>
            <MobileExplore onNavigate={() => setOpen(false)} />
            {LINKS.map((l, i) => (
              <motion.div key={l.label} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                <Link to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="block py-2 font-display text-3xl font-bold tracking-tight hover:text-terra transition-colors">{l.label}</Link>
              </motion.div>
            ))}
            <a href={`tel:+91${CONTACT.phone}`} className="mt-4 font-mono text-sm text-stone-600">{CONTACT.phoneDisplay}</a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
